import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Grid,
  Paper
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  Assessment as AssessmentIcon,
  Quiz as QuizIcon,
  Book as BookIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { auth, db } from '../firebase/config'
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc, 
  arrayRemove, 
  getDoc,
  deleteDoc,
  writeBatch
} from 'firebase/firestore'
import toast from 'react-hot-toast'

const StudentDetail = ({ studentData, classData, onBack, userData }) => {
  const { user } = useAuth()
  const [studentInfo, setStudentInfo] = useState(null)
  const [studentScore, setStudentScore] = useState(0)
  const [questionCount, setQuestionCount] = useState(0)
  const [documents, setDocuments] = useState([])
  const [evaluations, setEvaluations] = useState([])
  const [quizResults, setQuizResults] = useState([])
  const [wordTaskResults, setWordTaskResults] = useState([])
  const [loading, setLoading] = useState(true)
  const [totalQuizStats, setTotalQuizStats] = useState({ totalQuizzes: 0, totalQuestions: 0, totalCorrect: 0 })
  const [totalWordTaskStats, setTotalWordTaskStats] = useState({ totalTasks: 0, totalQuestions: 0, totalCorrect: 0 })
  const [scoreDialogOpen, setScoreDialogOpen] = useState(false)
  const [scoreAction, setScoreAction] = useState('add') // 'add' or 'subtract'
  const [scoreValue, setScoreValue] = useState('')
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
  const [resetQuestionDialogOpen, setResetQuestionDialogOpen] = useState(false)
  const [deleteEvaluationDialogOpen, setDeleteEvaluationDialogOpen] = useState(false)
  const [evaluationToDelete, setEvaluationToDelete] = useState(null)
  const [deleteAllEvaluationsDialogOpen, setDeleteAllEvaluationsDialogOpen] = useState(false)

  useEffect(() => {
    if (user && user.uid && studentData) {
      loadStudentData()
    }
  }, [user, studentData])

  const loadStudentData = async () => {
    if (!user || !user.uid) {
      console.log('User not ready yet')
      return
    }

    try {
      setLoading(true)
      
      // Load student info
      await loadStudentInfo()
      
      // Load question count
      await loadStudentQuestionCount()
      
      // Load documents
      await loadStudentDocuments()
      
      // Load productivity evaluations
      await loadProductivityEvaluations()
      
      // Load quiz results
      await loadMiniQuizResults()
      
      // Load word task results
      await loadWordTaskResults()
      
    } catch (error) {
      console.error('Error loading student data:', error)
      toast.error('Öğrenci verileri yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const loadStudentInfo = async () => {
    try {
      const studentDoc = await getDoc(doc(db, 'users', studentData.id))
      if (studentDoc.exists()) {
        const data = studentDoc.data()
        setStudentInfo(data)
        
        // Load class-based score
        await loadClassBasedScore(data)
      }
    } catch (error) {
      console.error('Error loading student info:', error)
    }
  }

  const loadClassBasedScore = async (studentData) => {
    try {
      const classScores = studentData.classScores || {}
      const currentClassId = classData?.id
      
      if (currentClassId && classScores[currentClassId]) {
        const scoreData = classScores[currentClassId]
        if (scoreData.teacherId === user.uid) {
          setStudentScore(scoreData.score || 0)
          return
        }
      }
      
      // If no class-specific score, find any score for this teacher
      for (const [classId, scoreData] of Object.entries(classScores)) {
        if (scoreData.teacherId === user.uid) {
          setStudentScore(scoreData.score || 0)
          return
        }
      }
      
      setStudentScore(0)
    } catch (error) {
      console.error('Error loading class-based score:', error)
      setStudentScore(0)
    }
  }

  const loadStudentQuestionCount = async () => {
    try {
      const questionsQuery = query(
        collection(db, 'studentQuestions'),
        where('studentId', '==', studentData.id),
        where('teacherId', '==', user.uid)
      )
      const snapshot = await getDocs(questionsQuery)
      setQuestionCount(snapshot.size)
    } catch (error) {
      console.error('Error loading question count:', error)
      setQuestionCount(0)
    }
  }

  const loadStudentDocuments = async () => {
    try {
      const documentsData = []
      
      // Load individual documents
      const individualQuery = query(
        collection(db, 'sentDocuments'),
        where('studentIds', 'array-contains', studentData.id),
        where('teacherId', '==', user.uid),
        where('documentType', '==', 'individual')
      )
      const individualSnapshot = await getDocs(individualQuery)
      
      for (const doc of individualSnapshot.docs) {
        const data = doc.data()
        const sourceClassId = data.sourceClassId
        
        // Filter by class if specified
        if (classData?.id && sourceClassId && sourceClassId !== classData.id) {
          continue
        }
        
        documentsData.push({
          id: doc.id,
          ...data,
          documentType: 'Öğrenciye Özel',
          className: data.sourceClassName || 'Öğrenciye Özel'
        })
      }
      
      // Load class documents
      if (studentData.classIds) {
        for (const classId of studentData.classIds) {
          // Filter by class if specified
          if (classData?.id && classId !== classData.id) {
            continue
          }
          
          const classDoc = await getDoc(doc(db, 'classes', classId))
          if (classDoc.exists()) {
            const className = classDoc.data().className
            
            const classQuery = query(
              collection(db, 'sentDocuments'),
              where('className', '==', className),
              where('teacherId', '==', user.uid),
              where('documentType', '==', 'class')
            )
            const classSnapshot = await getDocs(classQuery)
            
            for (const doc of classSnapshot.docs) {
              const data = doc.data()
              // Check if document already exists
              const exists = documentsData.find(d => d.id === doc.id)
              if (!exists) {
                documentsData.push({
                  id: doc.id,
                  ...data,
                  documentType: 'Sınıfa Gönderildi'
                })
              }
            }
          }
        }
      }
      
      // Sort by date (newest first)
      documentsData.sort((a, b) => {
        const dateA = a.sentDate?.toDate?.() || new Date(a.sentDate)
        const dateB = b.sentDate?.toDate?.() || new Date(b.sentDate)
        return dateB - dateA
      })
      
      setDocuments(documentsData)
    } catch (error) {
      console.error('Error loading documents:', error)
      setDocuments([])
    }
  }

  const loadProductivityEvaluations = async () => {
    try {
      const studentDoc = await getDoc(doc(db, 'users', studentData.id))
      if (studentDoc.exists()) {
        const data = studentDoc.data()
        const classEvaluations = data.classEvaluations || {}
        const evaluationsData = []
        
        for (const [classId, evalData] of Object.entries(classEvaluations)) {
          // Filter by class if specified
          if (classData?.id && classId !== classData.id) {
            continue
          }
          
          // Eski format (object) kontrolü
          if (!Array.isArray(evalData)) {
            if (evalData.teacherId === user.uid) {
              evaluationsData.push({
                id: `${classId}_${evalData.date}`,
                date: evalData.date,
                productive: evalData.productive
              })
            }
          } else {
            // Yeni format (array) kontrolü
            evalData.forEach((evaluation, index) => {
              if (evaluation.teacherId === user.uid) {
                evaluationsData.push({
                  id: `${classId}_${evaluation.date}_${index}`,
                  date: evaluation.date,
                  productive: evaluation.productive,
                  timestamp: evaluation.timestamp
                })
              }
            })
          }
        }
        
        // Sort by timestamp if available, otherwise by date (newest first)
        evaluationsData.sort((a, b) => {
          if (a.timestamp && b.timestamp) {
            return new Date(b.timestamp) - new Date(a.timestamp)
          }
          const dateA = new Date(a.date.split('.').reverse().join('-'))
          const dateB = new Date(b.date.split('.').reverse().join('-'))
          return dateB - dateA
        })
        
        setEvaluations(evaluationsData)
      }
    } catch (error) {
      console.error('Error loading evaluations:', error)
      setEvaluations([])
    }
  }

  const loadMiniQuizResults = async () => {
    try {
      const quizQuery = query(
        collection(db, 'quizResults'),
        where('studentId', '==', studentData.id),
        where('teacherId', '==', user.uid)
      )
      const snapshot = await getDocs(quizQuery)
      const results = []
      let totalQuizzes = 0
      let totalQuestions = 0
      let totalCorrect = 0
      
      for (const doc of snapshot.docs) {
        const data = doc.data()
        const resultClassId = data.classId
        
        // Filter by class if specified
        if (classData?.id && resultClassId && resultClassId !== classData.id) {
          continue
        }
        
        // Get quiz details
        let quizTitle = `Quiz ${data.quizId}`
        let shouldInclude = false
        
        try {
          // Handle quizId with slash
          let documentId = data.quizId
          if (data.quizId && data.quizId.includes('/')) {
            documentId = data.quizId.split('/')[0]
          }
          
          const quizDoc = await getDoc(doc(db, 'miniQuizzes', documentId))
          if (quizDoc.exists()) {
            const quizData = quizDoc.data()
            const quizClassId = quizData.classId
            const studentIds = quizData.studentIds || []
            const isIndividualSelection = quizData.isIndividualSelection || false
            
            // Check if quiz belongs to current class and student is assigned
            const classMatches = !classData?.id || (quizClassId && quizClassId === classData.id)
            const studentAssigned = studentIds.includes(studentData.id)
            
            if (classMatches && studentAssigned) {
              quizTitle = quizData.title || quizTitle
              shouldInclude = true
            }
          }
        } catch (error) {
          console.error('Error loading quiz details:', error)
          // If quiz details can't be loaded, still include it
          shouldInclude = true
        }
        
        if (shouldInclude) {
          const result = {
            id: doc.id,
            quizId: data.quizId,
            quizTitle,
            score: data.score || 0,
            totalQuestions: data.totalQuestions || 0,
            completionDate: data.completionDate?.toDate?.() || new Date(data.completionDate),
            userAnswers: data.userAnswers || [],
            questions: data.questions || []
          }
          
          results.push(result)
          totalQuizzes++
          totalQuestions += result.totalQuestions
          totalCorrect += result.score
        }
      }
      
      // Sort by completion date (newest first)
      results.sort((a, b) => b.completionDate - a.completionDate)
      setQuizResults(results)
      setTotalQuizStats({ totalQuizzes, totalQuestions, totalCorrect })
    } catch (error) {
      console.error('Error loading quiz results:', error)
      setQuizResults([])
      setTotalQuizStats({ totalQuizzes: 0, totalQuestions: 0, totalCorrect: 0 })
    }
  }

  const loadWordTaskResults = async () => {
    try {
      const wordTaskQuery = query(
        collection(db, 'wordTaskResults'),
        where('studentId', '==', studentData.id),
        where('teacherId', '==', user.uid)
      )
      const snapshot = await getDocs(wordTaskQuery)
      const results = []
      let totalTasks = 0
      let totalQuestions = 0
      let totalCorrect = 0
      
      for (const doc of snapshot.docs) {
        const data = doc.data()
        const resultClassId = data.classId
        
        // Filter by class if specified
        if (classData?.id && resultClassId && resultClassId !== classData.id) {
          continue
        }
        
        // Get word task details
        let wordTaskTitle = `Görev ${data.wordTaskId}`
        let wordTaskDescription = 'Görev detayları bulunamadı'
        let className = '-'
        let shouldInclude = false
        
        try {
          // Handle wordTaskId with slash
          let documentId = data.wordTaskId
          if (data.wordTaskId && data.wordTaskId.includes('/')) {
            documentId = data.wordTaskId.split('/')[0]
          }
          
          const wordTaskDoc = await getDoc(doc(db, 'wordTasks', documentId))
          if (wordTaskDoc.exists()) {
            const wordTaskData = wordTaskDoc.data()
            const wordTaskClassId = wordTaskData.classId
            const studentIds = wordTaskData.studentIds || []
            
            // Check if word task belongs to current class and student is assigned
            const classMatches = !classData?.id || (wordTaskClassId && wordTaskClassId === classData.id)
            const studentAssigned = studentIds.includes(studentData.id)
            
            if (classMatches && studentAssigned) {
              wordTaskTitle = wordTaskData.title || wordTaskTitle
              wordTaskDescription = wordTaskData.description || wordTaskDescription
              className = wordTaskData.className || className
              shouldInclude = true
            }
          }
        } catch (error) {
          console.error('Error loading word task details:', error)
          // If word task details can't be loaded, still include it
          shouldInclude = true
        }
        
        if (shouldInclude) {
          const result = {
            id: doc.id,
            wordTaskId: data.wordTaskId,
            wordTaskTitle,
            wordTaskDescription,
            className,
            score: data.score || 0,
            totalQuestions: data.totalQuestions || 0,
            completionDate: data.completionDate?.toDate?.() || new Date(data.completionDate),
            taskMode: data.taskMode || 'unknown',
            isIndividualSelection: data.isIndividualSelection || false
          }
          
          results.push(result)
          totalTasks++
          totalQuestions += result.totalQuestions
          totalCorrect += result.score
        }
      }
      
      // Sort by completion date (newest first)
      results.sort((a, b) => b.completionDate - a.completionDate)
      setWordTaskResults(results)
      setTotalWordTaskStats({ totalTasks, totalQuestions, totalCorrect })
    } catch (error) {
      console.error('Error loading word task results:', error)
      setWordTaskResults([])
      setTotalWordTaskStats({ totalTasks: 0, totalQuestions: 0, totalCorrect: 0 })
    }
  }

  const handleScoreUpdate = async () => {
    if (!scoreValue || isNaN(scoreValue)) {
      toast.error('Geçerli bir puan girin')
      return
    }

    try {
      const value = parseInt(scoreValue)
      let newScore = studentScore
      
      if (scoreAction === 'add') {
        newScore += value
      } else {
        newScore = Math.max(0, newScore - value)
      }

      // Update class-based score
      const studentDoc = await getDoc(doc(db, 'users', studentData.id))
      if (studentDoc.exists()) {
        const data = studentDoc.data()
        const classScores = data.classScores || {}
        const currentClassId = classData?.id
        
        if (currentClassId) {
          classScores[currentClassId] = {
            score: newScore,
            teacherId: user.uid,
            lastUpdated: new Date()
          }
          
          await updateDoc(doc(db, 'users', studentData.id), {
            classScores: classScores
          })
          
          setStudentScore(newScore)
          toast.success('Puan güncellendi')
          setScoreDialogOpen(false)
          setScoreValue('')
        } else {
          toast.error('Sınıf bilgisi bulunamadı')
        }
      }
    } catch (error) {
      console.error('Error updating score:', error)
      toast.error('Puan güncellenirken hata oluştu')
    }
  }

  const handleRemoveFromClass = async () => {
    try {
      if (classData?.id) {
        // Remove student from class
        await updateDoc(doc(db, 'classes', classData.id), {
          studentIds: arrayRemove(studentData.id)
        })
        
        // Remove class from student's classIds
        await updateDoc(doc(db, 'users', studentData.id), {
          classIds: arrayRemove(classData.id)
        })
        
        toast.success(`${studentData.username || studentData.email} sınıftan çıkarıldı`)
        onBack()
      } else {
        toast.error('Sınıf bilgisi bulunamadı')
      }
    } catch (error) {
      console.error('Error removing student from class:', error)
      toast.error('Öğrenci sınıftan çıkarılırken hata oluştu')
    }
  }

  const handleResetQuestionCount = async () => {
    try {
      const questionsQuery = query(
        collection(db, 'studentQuestions'),
        where('studentId', '==', studentData.id),
        where('teacherId', '==', user.uid)
      )
      const snapshot = await getDocs(questionsQuery)
      
      if (snapshot.empty) {
        toast.info('Sıfırlanacak soru bulunamadı')
        return
      }
      
      // Batch delete
      const batch = writeBatch(db)
      snapshot.docs.forEach(doc => {
        batch.delete(doc.ref)
      })
      
      await batch.commit()
      toast.success('Öğrencinin soru sayısı sıfırlandı')
      await loadStudentQuestionCount()
      setResetQuestionDialogOpen(false)
    } catch (error) {
      console.error('Error resetting question count:', error)
      toast.error('Soru sayısı sıfırlanırken hata oluştu')
    }
  }

  const handleDeleteEvaluation = async (evaluation) => {
    try {
      const studentDoc = await getDoc(doc(db, 'users', studentData.id))
      if (studentDoc.exists()) {
        const data = studentDoc.data()
        const classEvaluations = data.classEvaluations || {}
        
        // Remove the evaluation
        delete classEvaluations[evaluation.id]
        
        await updateDoc(doc(db, 'users', studentData.id), {
          classEvaluations: classEvaluations
        })
        
        setEvaluations(evaluations.filter(e => e.id !== evaluation.id))
        toast.success('Değerlendirme silindi')
        setDeleteEvaluationDialogOpen(false)
        setEvaluationToDelete(null)
      }
    } catch (error) {
      console.error('Error deleting evaluation:', error)
      toast.error('Değerlendirme silinirken hata oluştu')
    }
  }

  const handleQuickProductivityEvaluation = async (isProductive) => {
    try {
      const today = new Date().toLocaleDateString('tr-TR')
      const currentClassId = classData?.id
      
      if (!currentClassId) {
        toast.error('Sınıf bilgisi bulunamadı')
        return
      }
      
      const studentDoc = await getDoc(doc(db, 'users', studentData.id))
      if (studentDoc.exists()) {
        const data = studentDoc.data()
        const classEvaluations = data.classEvaluations || {}
        
        // Sınıf için evaluations array'i al veya oluştur
        if (!classEvaluations[currentClassId]) {
          classEvaluations[currentClassId] = []
        }
        
        // Eski format kontrolü - object ise array'e çevir
        if (!Array.isArray(classEvaluations[currentClassId])) {
          const oldEval = classEvaluations[currentClassId]
          classEvaluations[currentClassId] = [oldEval]
        }
        
        // Bugün için mevcut bir değerlendirme var mı kontrol et
        const existingEvalIndex = classEvaluations[currentClassId].findIndex(
          evaluation => evaluation.date === today && evaluation.teacherId === user.uid
        )
        
        const newEvaluation = {
          date: today,
          productive: isProductive,
          teacherId: user.uid,
          timestamp: new Date().toISOString()
        }
        
        if (existingEvalIndex >= 0) {
          // Mevcut değerlendirmeyi güncelle
          classEvaluations[currentClassId][existingEvalIndex] = newEvaluation
        } else {
          // Yeni değerlendirme ekle
          classEvaluations[currentClassId].push(newEvaluation)
        }
        
        await updateDoc(doc(db, 'users', studentData.id), {
          classEvaluations: classEvaluations
        })
        
        // Reload evaluations to show the new one
        await loadProductivityEvaluations()
        
        toast.success(`Öğrenci ${isProductive ? 'verimli' : 'verimsiz'} olarak işaretlendi`)
        console.log('📊 StudentDetail - Verimlilik değerlendirmesi kaydedildi:', newEvaluation)
      }
    } catch (error) {
      console.error('Error adding productivity evaluation:', error)
      toast.error('Değerlendirme eklenirken hata oluştu')
    }
  }

  const handleDeleteAllEvaluations = async () => {
    try {
      const studentDoc = await getDoc(doc(db, 'users', studentData.id))
      if (studentDoc.exists()) {
        const data = studentDoc.data()
        const classEvaluations = data.classEvaluations || {}
        
        // Remove all evaluations for this teacher and class
        const currentClassId = classData?.id
        const updatedEvaluations = {}
        
        for (const [classId, evalData] of Object.entries(classEvaluations)) {
          if (evalData.teacherId !== user.uid || (currentClassId && classId !== currentClassId)) {
            updatedEvaluations[classId] = evalData
          }
        }
        
        await updateDoc(doc(db, 'users', studentData.id), {
          classEvaluations: updatedEvaluations
        })
        
        setEvaluations([])
        toast.success('Tüm değerlendirmeler silindi')
        setDeleteAllEvaluationsDialogOpen(false)
      }
    } catch (error) {
      console.error('Error deleting all evaluations:', error)
      toast.error('Değerlendirmeler silinirken hata oluştu')
    }
  }

  const openDocument = (document) => {
    try {
      window.open(document.documentUrl, '_blank')
    } catch (error) {
      console.error('Error opening document:', error)
      toast.error('Döküman açılamadı')
    }
  }

  const showQuizResultDetail = (result) => {
    let detail = `Quiz: ${result.quizTitle}\n\n`
    
    if (result.questions && result.userAnswers) {
      for (let i = 0; i < result.questions.length; i++) {
        const question = result.questions[i]
        const correct = question.correctAnswer
        const studentAnswer = result.userAnswers[i] || '-'
        const isCorrect = correct === studentAnswer
        
        detail += `Soru ${i + 1}: ${question.question}\n`
        detail += `Öğrenci cevabı: ${studentAnswer} ${isCorrect ? '✅' : '❌'}\n`
        detail += `Doğru cevap: ${correct}\n\n`
      }
    }
    
    alert(detail)
  }

  const showWordTaskResultDetail = (result) => {
    let detail = `Görev: ${result.wordTaskTitle}\n\n`
    
    if (result.wordTaskDescription) {
      detail += `Açıklama: ${result.wordTaskDescription}\n\n`
    }
    
    detail += `Mod: ${result.taskMode === 'matching' ? 'Eşleştirme Oyunu' : 
                     result.taskMode === 'meaning' ? 'Anlam Sorma' : 'Bilinmiyor'}\n\n`
    
    detail += `Sonuç: ${result.score}/${result.totalQuestions}\n`
    detail += `Doğru: ${result.score}\n`
    detail += `Yanlış: ${result.totalQuestions - result.score}\n`
    
    if (result.totalQuestions > 0) {
      const successRate = (result.score / result.totalQuestions) * 100
      detail += `Başarı oranı: %${successRate.toFixed(1)}\n`
    }
    
    if (result.completionDate) {
      detail += `\nTamamlanma: ${result.completionDate.toLocaleString('tr-TR')}`
    }
    
    alert(detail)
  }

  const getProductivityStats = () => {
    const total = evaluations.length
    const productive = evaluations.filter(e => e.productive).length
    const unproductive = total - productive
    
    if (total === 0) {
      return 'Henüz değerlendirme yapılmamış'
    }
    
    const productivePercentage = (productive / total) * 100
    const unproductivePercentage = 100 - productivePercentage
    
    return `📊 Toplam Değerlendirme: ${total}\n` +
           `✅ Verimli: ${productive} (${productivePercentage.toFixed(1)}%)\n` +
           `❌ Verimsiz: ${unproductive} (${unproductivePercentage.toFixed(1)}%)`
  }

  const getQuizStats = () => {
    if (totalQuizStats.totalQuizzes === 0) {
      return 'Bu sınıfta henüz quiz çözülmemiş'
    }
    
    const { totalQuizzes, totalQuestions, totalCorrect } = totalQuizStats
    const totalWrong = totalQuestions - totalCorrect
    const successRate = totalQuestions > 0 ? (totalCorrect / totalQuestions) * 100 : 0
    
    return `📝 Bu Sınıfta Quiz: ${totalQuizzes} adet\n` +
           `❓ Bu Sınıfta Soru: ${totalQuestions} adet\n` +
           `✅ Bu Sınıfta Doğru: ${totalCorrect} adet\n` +
           `❌ Bu Sınıfta Yanlış: ${totalWrong} adet\n` +
           `📊 Bu Sınıfta Başarı: ${successRate.toFixed(1)}%`
  }

  const getWordTaskStats = () => {
    if (totalWordTaskStats.totalTasks === 0) {
      return 'Bu sınıfta henüz kelime görevi çözülmemiş'
    }
    
    const { totalTasks, totalQuestions, totalCorrect } = totalWordTaskStats
    const totalWrong = totalQuestions - totalCorrect
    const successRate = totalQuestions > 0 ? (totalCorrect / totalQuestions) * 100 : 0
    
    return `📚 Bu Sınıfta Görev: ${totalTasks} adet\n` +
           `❓ Bu Sınıfta Soru: ${totalQuestions} adet\n` +
           `✅ Bu Sınıfta Doğru: ${totalCorrect} adet\n` +
           `❌ Bu Sınıfta Yanlış: ${totalWrong} adet\n` +
           `📊 Bu Sınıfta Başarı: ${successRate.toFixed(1)}%`
  }

  if (!user || !user.uid) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography>Kullanıcı bilgileri yükleniyor...</Typography>
      </Box>
    )
  }

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography>Öğrenci verileri yükleniyor...</Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    )
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* App Bar */}
      <AppBar position="static">
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={onBack}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              {studentData.username || studentData.email}
            </Typography>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Container maxWidth="md" sx={{ py: 3 }}>
        {/* Student Info Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
              Öğrenci Bilgileri
            </Typography>
            
            <Typography variant="h6" fontWeight="bold" color="text.primary" gutterBottom>
              {studentData.username || studentData.email}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {studentData.email}
            </Typography>
            
            <Chip
              label={`Puan: ${studentScore}`}
              color="warning"
              sx={{ mt: 1, mb: 2 }}
            />
            
                         <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
               <Typography variant="body1" fontWeight="bold" color="info.main" sx={{ flexGrow: 1 }}>
                 Gönderilen Soru: {questionCount}
               </Typography>
               <Button
                 variant="outlined"
                 size="small"
                 onClick={() => setResetQuestionDialogOpen(true)}
                 color="error"
                 startIcon={<DeleteIcon />}
                 sx={{ ml: 1 }}
               >
                 Sıfırla
               </Button>
             </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setScoreAction('add')
                    setScoreDialogOpen(true)
                  }}
                >
                  Puan Ver
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<RemoveIcon />}
                  onClick={() => {
                    setScoreAction('subtract')
                    setScoreDialogOpen(true)
                  }}
                >
                  Puan Çıkar
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  fullWidth
                  color="error"
                  onClick={() => setRemoveDialogOpen(true)}
                >
                  Sınıftan Çıkar
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Productivity Evaluation Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold" color="primary">
                Günlük Verimlilik Değerlendirmesi
              </Typography>
              {evaluations.length > 0 && (
                <IconButton
                  size="small"
                  onClick={() => setDeleteAllEvaluationsDialogOpen(true)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
            
            {/* Quick Productivity Buttons - Only visible to teachers */}
            {userData?.role === 'teacher' && (
              <Box sx={{ display: 'flex', gap: 2, mb: 3, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<AddIcon />}
                  onClick={() => handleQuickProductivityEvaluation(true)}
                  sx={{
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                      backgroundColor: 'success.light',
                      color: 'success.contrastText'
                    }
                  }}
                >
                  Verimli
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<RemoveIcon />}
                  onClick={() => handleQuickProductivityEvaluation(false)}
                  sx={{
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                      backgroundColor: 'error.light',
                      color: 'error.contrastText'
                    }
                  }}
                >
                  Verimsiz
                </Button>
              </Box>
            )}
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {userData?.role === 'teacher' 
                ? 'Hızlı değerlendirme için yukarıdaki butonları kullanın veya öğrenci listesindeki + ve - butonlarını kullanın'
                : 'Hızlı değerlendirme için öğrenci listesindeki + ve - butonlarını kullanın'
              }
            </Typography>
            
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Typography variant="body2" whiteSpace="pre-line">
                {getProductivityStats()}
              </Typography>
            </Paper>
            
            {evaluations.length > 0 && (
              <>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Önceki Değerlendirmeler
                </Typography>
                <List dense>
                  {evaluations.map((evaluation, index) => (
                    <ListItem
                      key={evaluation.id}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => {
                            setEvaluationToDelete(evaluation)
                            setDeleteEvaluationDialogOpen(true)
                          }}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar>
                          {evaluation.productive ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={evaluation.productive ? 'Verimli' : 'Verimsiz'}
                        secondary={evaluation.date}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </CardContent>
        </Card>

        {/* Documents Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
              Gönderilen Dökümanlar
            </Typography>
            
            {documents.length === 0 ? (
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Bu öğrenciye henüz döküman gönderilmemiş
              </Typography>
            ) : (
              <List dense>
                {documents.map((doc, index) => (
                  <ListItem
                    key={doc.id}
                    button
                    onClick={() => openDocument(doc)}
                  >
                    <ListItemAvatar>
                      <Avatar>
                        <DescriptionIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={doc.documentName || doc.title || 'Döküman Adı'}
                      secondary={
                        <>
                          {doc.sentDate && (
                            `Gönderilme: ${doc.sentDate.toDate?.()?.toLocaleString('tr-TR') || doc.sentDate}`
                          )}
                          <br />
                          <Chip
                            label={doc.documentType}
                            size="small"
                            color={doc.documentType === 'Öğrenciye Özel' ? 'primary' : 'secondary'}
                            sx={{ mt: 0.5 }}
                          />
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>

                 {/* Quiz Results Section */}
         <Card sx={{ mb: 3 }}>
           <CardContent>
             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
               <Typography variant="h6" fontWeight="bold" color="primary">
                 Mini Quiz Sonuçları
               </Typography>
               <Chip
                 label={`${totalQuizStats.totalQuizzes} Quiz`}
                 color="secondary"
                 size="small"
               />
             </Box>
             
             <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
               <Typography variant="body2" whiteSpace="pre-line">
                 {getQuizStats()}
               </Typography>
             </Paper>
             
             {quizResults.length > 0 ? (
               <List dense>
                 {quizResults.map((result, index) => (
                   <ListItem
                     key={result.id}
                     button
                     onClick={() => showQuizResultDetail(result)}
                     sx={{ 
                       border: 1, 
                       borderColor: 'grey.200', 
                       borderRadius: 1, 
                       mb: 1,
                       '&:hover': {
                         borderColor: 'primary.main',
                         bgcolor: 'grey.50'
                       }
                     }}
                   >
                     <ListItemAvatar>
                       <Avatar sx={{ bgcolor: 'primary.main' }}>
                         <QuizIcon />
                       </Avatar>
                     </ListItemAvatar>
                     <ListItemText
                       primary={
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                           <Typography variant="subtitle1" fontWeight="bold">
                             {result.quizTitle}
                           </Typography>
                           <Chip
                             label={`${result.score}/${result.totalQuestions}`}
                             size="small"
                             color={result.score === result.totalQuestions ? 'success' : 'warning'}
                             variant="filled"
                           />
                         </Box>
                       }
                       secondary={
                         <>
                           {result.completionDate && (
                             `Tamamlanma: ${result.completionDate.toLocaleString('tr-TR')}`
                           )}
                           {result.isIndividualSelection && (
                             <Chip
                               label="Bireysel Seçim"
                               size="small"
                               variant="outlined"
                               sx={{ ml: 1, mt: 0.5 }}
                             />
                           )}
                         </>
                       }
                     />
                   </ListItem>
                 ))}
               </List>
             ) : (
               <Box sx={{ textAlign: 'center', py: 3 }}>
                 <QuizIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                 <Typography variant="body2" color="text.secondary">
                   Bu sınıfta henüz quiz çözülmemiş
                 </Typography>
               </Box>
             )}
           </CardContent>
         </Card>

                 {/* Word Task Results Section */}
         <Card sx={{ mb: 3 }}>
           <CardContent>
             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
               <Typography variant="h6" fontWeight="bold" color="primary">
                 Kelime Görevleri Sonuçları
               </Typography>
               <Chip
                 label={`${totalWordTaskStats.totalTasks} Görev`}
                 color="secondary"
                 size="small"
               />
             </Box>
             
             <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
               <Typography variant="body2" whiteSpace="pre-line">
                 {getWordTaskStats()}
               </Typography>
             </Paper>
             
             {wordTaskResults.length > 0 ? (
               <List dense>
                 {wordTaskResults.map((result, index) => (
                   <ListItem
                     key={result.id}
                     button
                     onClick={() => showWordTaskResultDetail(result)}
                     sx={{ 
                       border: 1, 
                       borderColor: 'grey.200', 
                       borderRadius: 1, 
                       mb: 1,
                       '&:hover': {
                         borderColor: 'primary.main',
                         bgcolor: 'grey.50'
                       }
                     }}
                   >
                     <ListItemAvatar>
                       <Avatar sx={{ bgcolor: 'secondary.main' }}>
                         <BookIcon />
                       </Avatar>
                     </ListItemAvatar>
                     <ListItemText
                       primary={
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                           <Typography variant="subtitle1" fontWeight="bold">
                             {result.wordTaskTitle}
                           </Typography>
                           <Chip
                             label={`${result.score}/${result.totalQuestions}`}
                             size="small"
                             color={result.score === result.totalQuestions ? 'success' : 'warning'}
                             variant="filled"
                           />
                         </Box>
                       }
                       secondary={
                         <>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                             <Chip
                               label={result.taskMode === 'matching' ? 'Eşleştirme' : 
                                      result.taskMode === 'meaning' ? 'Anlam Sorma' : 'Bilinmiyor'}
                               size="small"
                               variant="outlined"
                               color="info"
                             />
                             {result.isIndividualSelection && (
                               <Chip
                                 label="Bireysel Seçim"
                                 size="small"
                                 variant="outlined"
                               />
                             )}
                           </Box>
                           {result.completionDate && (
                             `Tamamlanma: ${result.completionDate.toLocaleString('tr-TR')}`
                           )}
                         </>
                       }
                     />
                   </ListItem>
                 ))}
               </List>
             ) : (
               <Box sx={{ textAlign: 'center', py: 3 }}>
                 <BookIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                 <Typography variant="body2" color="text.secondary">
                   Bu sınıfta henüz kelime görevi çözülmemiş
                 </Typography>
               </Box>
             )}
           </CardContent>
         </Card>
      </Container>

      {/* Score Dialog */}
      <Dialog open={scoreDialogOpen} onClose={() => setScoreDialogOpen(false)}>
        <DialogTitle>
          {scoreAction === 'add' ? 'Puan Ver' : 'Puan Çıkar'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={scoreAction === 'add' ? 'Eklenecek puan' : 'Çıkarılacak puan'}
            type="number"
            fullWidth
            variant="outlined"
            value={scoreValue}
            onChange={(e) => setScoreValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScoreDialogOpen(false)}>
            İptal
          </Button>
          <Button onClick={handleScoreUpdate} variant="contained">
            {scoreAction === 'add' ? 'Ekle' : 'Çıkar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove from Class Dialog */}
      <Dialog open={removeDialogOpen} onClose={() => setRemoveDialogOpen(false)}>
        <DialogTitle>Sınıftan Çıkar</DialogTitle>
        <DialogContent>
          <Typography>
            {studentData.username || studentData.email} adlı öğrenciyi sınıftan çıkarmak istediğinizden emin misiniz?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogOpen(false)}>
            İptal
          </Button>
          <Button onClick={handleRemoveFromClass} color="error" variant="contained">
            Çıkar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Question Count Dialog */}
      <Dialog open={resetQuestionDialogOpen} onClose={() => setResetQuestionDialogOpen(false)}>
        <DialogTitle>Öğrenci Soru Sayısını Sıfırla</DialogTitle>
        <DialogContent>
          <Typography>
            {studentData.username || studentData.email} adlı öğrencinin size gönderdiği soru sayısını sıfırlamak istediğinizden emin misiniz?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetQuestionDialogOpen(false)}>
            İptal
          </Button>
          <Button onClick={handleResetQuestionCount} color="warning" variant="contained">
            Sıfırla
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Evaluation Dialog */}
      <Dialog open={deleteEvaluationDialogOpen} onClose={() => setDeleteEvaluationDialogOpen(false)}>
        <DialogTitle>Değerlendirmeyi Sil</DialogTitle>
        <DialogContent>
          <Typography>
            Bu değerlendirmeyi silmek istediğinizden emin misiniz?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteEvaluationDialogOpen(false)}>
            İptal
          </Button>
          <Button onClick={() => handleDeleteEvaluation(evaluationToDelete)} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Evaluations Dialog */}
      <Dialog open={deleteAllEvaluationsDialogOpen} onClose={() => setDeleteAllEvaluationsDialogOpen(false)}>
        <DialogTitle>Tüm Değerlendirmeleri Sil</DialogTitle>
        <DialogContent>
          <Typography>
            {studentData.username || studentData.email} adlı öğrencinin tüm verimlilik değerlendirmelerini silmek istediğinizden emin misiniz?
            <br /><br />
            Bu işlem geri alınamaz!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAllEvaluationsDialogOpen(false)}>
            İptal
          </Button>
          <Button onClick={handleDeleteAllEvaluations} color="error" variant="contained">
            Tümünü Sil
          </Button>
        </DialogActions>
      </Dialog>
  </Box>
  )
}

export default StudentDetail
